import React from 'react';
import ReactSelect from 'react-select';
import { css } from 'styled-components';
import { Caption, Body1 } from '../Typography/Typography';
import Box from '../Box/Box';
import CheckIcon from '../../images/icons/check.svg';
import ChevronDownIcon from '../../images/icons/caret-down.svg';
import { theme } from '../../../config/theme';
import { rem } from 'polished';

const Option = ({ innerRef, innerProps, isFocused, isSelected, children }) => {
    return (
        <Box
            ref={innerRef}
            {...innerProps}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderRadius="4px"
            p="8px"
            bg={isFocused && 'neutral.100'}
            css={css`
                cursor: pointer;
            `}
        >
            <Caption>{children}</Caption>
            {isSelected && (
                <Box color="primary.main">
                    <CheckIcon width="17" height="17" />
                </Box>
            )}
        </Box>
    );
};

const DropdownIndicator = ({ innerRef, innerProps, isFocused }) => {
    return (
        <Box
            ref={innerRef}
            {...innerProps}
            display="flex"
            color={isFocused ? 'primary.main' : 'rgba(0,0,0,.5)'}
            marginRight="7px"
        >
            <ChevronDownIcon />
        </Box>
    );
};

const getDefaultStyles = (hasError: boolean) => ({
    indicatorSeparator: () => ({ display: 'none;' }),
    control: (provided, state) => ({
        ...provided,
        backgroundColor: 'white',
        padding: '8px',
        fontSize: '14px',
        fontWeight: 300,
        transition: 'border 0.2s ease-out',
        borderRadius: '4px',
        background: theme.colors.neutral[100],
        border: `1px solid ${
            hasError ? theme.colors.error[700] : 'transparent'
        }`,
        '&:hover': {
            borderColor: hasError
                ? theme.colors.error[700]
                : state.isFocused
                ? theme.colors.primary.main
                : 'transparent',
        },
        ...(state.isFocused && {
            border: `1px solid ${
                hasError ? theme.colors.error[700] : theme.colors.primary.main
            }`,
            boxShadow: 'initial',
        }),
        zIndex: state.isFocused && 10,
        cursor: 'text',
        minHeight: 'auto',
    }),
    menuList: (provided) => ({
        ...provided,
        maxHeight: '140px',
        paddingBlock: '8px',
        paddingInline: '4px',
        '::-webkit-scrollbar': { width: '6px', height: '6px' },
        '::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            background: theme.colors.neutral[400],
        },
        '::-webkit-scrollbar-thumb:hover': {
            background: theme.colors.neutral[500],
        },
        '::-webkit-scrollbar-thumb:active': {
            background: theme.colors.neutral[700],
        },
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: 0,
    }),
    placeholder: (provided) => ({
        ...provided,
        color: theme.colors.neutral[1400],
    }),
    input: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
    }),
});

const getVariantStyles = (hasError) => ({
    default: getDefaultStyles(hasError),
    outline: {
        ...getDefaultStyles(hasError),
        control: (provided, state) => ({
            ...provided,
            border: 'none',
            borderBottom: `solid 2px ${
                state.isFocused ? theme.colors.primary.main : 'rgba(0,0,0,.12)'
            }`,
            borderRadius: 0,
            boxShadow: 'none',
        }),
    },
});

const Select = ({ variant = 'default', error, index, label, ...props }) => {
    return (
        <Box>
            <Box as={Caption} display="block" mb={rem(4)}>
                {label}
            </Box>
            <ReactSelect
                styles={getVariantStyles(error)[variant]}
                css={css`
                    z-index: ${index + 1};
                    &:focus-within {
                        z-index: 50;
                    }
                `}
                {...props}
            />
        </Box>
    );
};

Select.defaultProps = {
    components: { Option, DropdownIndicator },
    formatOptionLabel: ({ label }) => <Body1>{label}</Body1>,
};

export default Select;
